import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Stage 4(d) VOD playback modal. Loads /api/live/{streamId}/vod for metadata,
 * then renders a <video> pointed at /api/live/{streamId}/vod/stream?auth=<jwt>.
 * The token stays server-side (httpOnly cookie); we fetch a short-lived
 * token via /api/auth/vod-ticket if available, otherwise we let the video
 * tag use the httpOnly cookie via credentials mode.
 */
export default function VodPlayerModal({ open, onOpenChange, streamId, token }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !streamId) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/api/live/${streamId}/vod`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setMeta(data))
      .catch((e) => {
        setError(String(e.message || e));
        toast.error("Could not load recording");
      })
      .finally(() => setLoading(false));
  }, [open, streamId, token]);

  const videoUrl = useMemo(() => {
    if (!meta || !meta.stream_url) return null;
    // The <video> tag can't send Authorization headers, so we bridge with
    // ?auth=<jwt>. If we don't have a JWT in props we fall back to cookie
    // credentials mode (backend already accepts httpOnly cookie).
    const qs = token ? `?auth=${encodeURIComponent(token)}` : "";
    return `${API}${meta.stream_url}${qs}`;
  }, [meta, token]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="vod-player-modal"
        className="max-w-3xl bg-zinc-950 border-white/10 rounded-none"
      >
        <DialogHeader>
          <DialogTitle className="text-sm font-medium tracking-wide">Recording</DialogTitle>
        </DialogHeader>

        {loading && (
          <div data-testid="vod-loading" className="flex items-center gap-2 text-zinc-400 text-sm py-12 justify-center">
            <Loader2 className="animate-spin" size={16} /> Loading recording…
          </div>
        )}

        {!loading && error && (
          <div data-testid="vod-error" className="text-rose-400 text-sm py-6 flex items-center gap-2">
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {!loading && !error && meta && meta.status === "none" && (
          <div data-testid="vod-none" className="text-zinc-500 text-sm py-6 text-center">
            No recording available for this stream.
          </div>
        )}

        {!loading && !error && meta && meta.status && ["pending", "in_progress"].includes(meta.status) && (
          <div data-testid="vod-pending" className="text-zinc-400 text-sm py-6 text-center">
            Recording is still finalizing. Please check back in a minute.
          </div>
        )}

        {!loading && !error && meta && ["failed", "aborted"].includes(meta.status) && (
          <div data-testid="vod-failed" className="text-rose-400 text-sm py-6 flex items-center gap-2">
            <AlertTriangle size={14} />
            Recording could not be finalized{meta.error_message ? `: ${meta.error_message}` : "."}
          </div>
        )}

        {!loading && !error && meta && ["ready", "partial"].includes(meta.status) && videoUrl && (
          <div className="space-y-2">
            {meta.status === "partial" && (
              <div className="text-amber-400 text-xs flex items-center gap-1">
                <AlertTriangle size={12} /> Partial recording — some of the stream may be missing.
              </div>
            )}
            <video
              data-testid="vod-video"
              src={videoUrl}
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-video bg-black rounded-none"
            />
            <div className="text-xs text-zinc-500">
              {meta.duration_ms > 0 && <>Duration: {Math.round(meta.duration_ms / 1000)}s · </>}
              {meta.size_bytes > 0 && <>Size: {(meta.size_bytes / (1024 * 1024)).toFixed(1)} MB</>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
